import { List, Map } from 'immutable';
import type { Moment } from 'moment';

import {
  Entity,
  type EntityFields,
  type EntityRecord,
  Fields,
} from '@burnsred/entity';
import { default as DjangoRestFramework } from '@burnsred/entity-duck-namespace-drf';

import { duckAppName } from '@/constants';

import type { MockConditionMode } from './enums';
import MockEndpointRefEntity, {
  type MockEndpointRefEntityRecord,
} from './MockEndpointRef';

class MockResponseEntity extends Entity {
  static paths = {
    apiBase: '/mock_api/v1/mock_response/',
  };

  static fields: EntityFields<MockResponseEntityFields> = {
    uuid: new Fields.IdField(),
    endpoint: new Fields.EntityField({ entity: MockEndpointRefEntity }),
    name: new Fields.CharField({ blank: true }),
    sort_order: new Fields.IntegerField({ default: 0 }),
    is_default: new Fields.BooleanField({ default: false }),
    is_active: new Fields.BooleanField({ default: true }),
    conditions: new Fields.AnyField({ blank: true, default: () => List() }),
    condition_mode: new Fields.CharField({ default: 'all' }),
    status_code: new Fields.IntegerField({ default: 200 }),
    content_type: new Fields.CharField({ default: 'application/json' }),
    headers: new Fields.AnyField({ blank: true, default: () => Map() }),
    body: new Fields.TextField({ blank: true }),
    is_template: new Fields.BooleanField({ default: false }),
    delay_ms: new Fields.IntegerField({ blank: true }),
    warnings: new Fields.AnyField({ blank: true, local: true }),
    can_edit: new Fields.BooleanField({ blank: true, local: true }),
    created: new Fields.DateTimeField({ blank: true, local: true }),
    modified: new Fields.DateTimeField({ blank: true, local: true }),
  };

  static dataToRecord(data: Partial<MockResponseEntityData>) {
    return super.dataToRecord(data) as MockResponseEntityRecord;
  }

  static toString(record: MockResponseEntityRecord | null | undefined) {
    if (!record) return '';
    return record.get('name') || String(record.get('status_code') ?? '');
  }
}

export type MockConditionValue = string | number | string[] | null;

export type MockCondition = {
  source: string;
  key: string;
  operator: string;
  value?: MockConditionValue;
  negate?: boolean;
};

export type MockResponseEntityFields = {
  uuid: string;
  endpoint: MockEndpointRefEntityRecord;
  name: string;
  sort_order: number;
  is_default: boolean;
  is_active: boolean;
  conditions: List<Map<string, unknown>>;
  condition_mode: MockConditionMode;
  status_code: number;
  content_type: string;
  headers: Map<string, string>;
  body: string;
  is_template: boolean;
  delay_ms: number | null;
  warnings: List<string> | null;
  can_edit: boolean;
  created: Moment | null;
  modified: Moment | null;
};

export type MockResponseEntityData = Omit<
  MockResponseEntityFields,
  'endpoint' | 'conditions' | 'headers' | 'warnings' | 'created' | 'modified'
> & {
  endpoint: { uuid: string; method?: string; path_pattern?: string };
  conditions: MockCondition[];
  headers: Record<string, string>;
  warnings: string[];
  created: string;
  modified: string;
};

export type MockResponseEntityRecord = EntityRecord<MockResponseEntityFields>;

MockResponseEntity.duck = new DjangoRestFramework({
  app: duckAppName,
  entity: MockResponseEntity,
  name: 'MockResponseEntity',
});

export default MockResponseEntity;
